/**
 * Security Settings Component
 * Epic #87: MFA Disable - Carbon Design System implementation
 * Handles MFA status display and disable functionality
 * All styling uses Carbon Design tokens via SCSS module
 */

import React, { useState, useEffect } from 'react';
import {
  Tile,
  Button,
  Modal,
  TextInput,
  InlineNotification,
  Stack,
  Toggle,
  Loading,
  PasswordInput,
} from '@carbon/react';
import { Security, Locked, Unlocked, Checkmark, Warning } from '@carbon/icons-react';
import axios from 'axios';
import { MFASetup } from '../auth/MFASetup';
import styles from './SecuritySettings.module.scss';

interface MFAStatus {
  mfa_enabled: boolean;
  mfa_method: string | null;
  has_backup_codes: boolean;
  unused_backup_codes: number;
}

interface SecuritySettingsProps {
  onMFAChange?: (enabled: boolean) => void;
}

export const SecuritySettings: React.FC<SecuritySettingsProps> = ({ onMFAChange }) => {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [enablePasswordModalOpen, setEnablePasswordModalOpen] = useState(false);
  const [mfaSetupModalOpen, setMfaSetupModalOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const API_BASE_URL = process.env.VITE_API_BASE_URL || '/api';

  useEffect(() => {
    fetchUserInfo();
    fetchMFAStatus();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUserId(response.data.data.user_id);
      setUserEmail(response.data.data.email);
    } catch (err) {
      // Silently fail - user info is not critical for security settings display
    }
  };

  const fetchMFAStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/auth/mfa/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setMfaStatus(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }

    if (!confirmation) {
      setError('Please confirm that you want to disable MFA');
      return;
    }

    setDisabling(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/mfa/disable`,
        {
          password,
          confirmation: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess(response.data.message);
      setDisableModalOpen(false);

      // Refresh MFA status
      await fetchMFAStatus();

      // Notify parent component
      if (onMFAChange) {
        onMFAChange(false);
      }

      // Show success message and redirect to login after 3 seconds
      setTimeout(() => {
        alert('All sessions have been invalidated. Please log in again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setDisabling(false);
    }
  };

  const handleOpenDisableModal = () => {
    setDisableModalOpen(true);
    setPassword('');
    setConfirmation(false);
    setError(null);
  };

  const handleCloseDisableModal = () => {
    setDisableModalOpen(false);
    setPassword('');
    setConfirmation(false);
    setError(null);
  };

  const handleOpenEnablePasswordModal = () => {
    setEnablePasswordModalOpen(true);
    setPassword('');
    setError(null);
  };

  const handleCloseEnablePasswordModal = () => {
    setEnablePasswordModalOpen(false);
    setPassword('');
    setError(null);
  };

  const handleVerifyPasswordForMFA = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }

    setVerifyingPassword(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      // Verify password by calling the backend
      await axios.post(
        `${API_BASE_URL}/api/v1/auth/verify-password`,
        { password },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEnablePasswordModalOpen(false);
      setMfaSetupModalOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleMFASetupComplete = async (success: boolean) => {
    if (success) {
      setSuccess('Two-factor authentication enabled successfully!');
      await fetchMFAStatus();

      if (onMFAChange) {
        onMFAChange(true);
      }
    }
    setMfaSetupModalOpen(false);
  };

  if (loading) {
    return (
      <Tile className={styles['loading-container']}>
        <Loading description="Loading security settings..." withOverlay={false} />
      </Tile>
    );
  }

  return (
    <>
      <Tile className={styles['security-tile']}>
        <div className={styles.header}>
          <Security size={24} className={styles['header-icon']} />
          <h3 className="cds--type-heading-compact-02">Security Settings</h3>
        </div>

        {success && (
          <InlineNotification
            kind="success"
            title="Success"
            subtitle={success}
            lowContrast
            className={styles.notification}
            onCloseButtonClick={() => setSuccess(null)}
          />
        )}

        {error && !disableModalOpen && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            className={styles.notification}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        {/* Two-Factor Authentication Section */}
        <div className={styles['mfa-section']}>
          <div
            className={`${styles['mfa-status-card']} ${mfaStatus?.mfa_enabled ? styles['mfa-status-card-enabled'] : styles['mfa-status-card-disabled']}`}
          >
            <div className={styles['mfa-status-content']}>
              {mfaStatus?.mfa_enabled ? (
                <Locked size={20} className={`${styles['mfa-status-icon']} ${styles['mfa-status-icon-enabled']}`} />
              ) : (
                <Unlocked size={20} className={`${styles['mfa-status-icon']} ${styles['mfa-status-icon-disabled']}`} />
              )}
              <div>
                <h4 className={`cds--type-heading-compact-01 ${styles['mfa-status-title']}`}>
                  Two-Factor Authentication
                </h4>
                <p className={styles['mfa-status-description']}>
                  {mfaStatus?.mfa_enabled
                    ? `Status: Enabled (${mfaStatus.mfa_method?.toUpperCase()})`
                    : 'Status: Disabled'}
                </p>
              </div>
            </div>
            <div>
              {mfaStatus?.mfa_enabled ? (
                <Button kind="danger" size="sm" onClick={handleOpenDisableModal}>
                  Disable MFA
                </Button>
              ) : (
                <Button kind="primary" size="sm" renderIcon={Security} onClick={handleOpenEnablePasswordModal}>
                  Enable MFA
                </Button>
              )}
            </div>
          </div>

          {mfaStatus?.mfa_enabled && (
            <div className={styles['backup-codes-info']}>
              <InlineNotification
                kind="info"
                title="Security Information"
                subtitle={`You have ${mfaStatus.unused_backup_codes || 0} unused backup codes remaining.`}
                lowContrast
                hideCloseButton
              />
            </div>
          )}
        </div>

        {/* Additional Security Options */}
        <div className={styles['additional-security']}>
          <h4 className={`cds--type-heading-compact-01 ${styles['additional-security-title']}`}>
            Additional Security Options
          </h4>

          <div className={styles['security-option']}>
            <div>
              <p className="cds--type-body-compact-01">Session timeout after inactivity</p>
              <p className={styles['security-option-label']}>Automatically log out after 30 minutes of inactivity</p>
            </div>
            <Toggle id="session-timeout" labelText="" defaultToggled={true} size="sm" />
          </div>

          <div className={styles['security-option']}>
            <div>
              <p className="cds--type-body-compact-01">Email login notifications</p>
              <p className={styles['security-option-label']}>Receive email when someone logs into your account</p>
            </div>
            <Toggle id="email-notifications" labelText="" defaultToggled={true} size="sm" />
          </div>

          <div className={styles['security-option']}>
            <div>
              <p className="cds--type-body-compact-01">Suspicious activity alerts</p>
              <p className={styles['security-option-label']}>Get notified of unusual login attempts</p>
            </div>
            <Toggle id="suspicious-activity" labelText="" defaultToggled={true} size="sm" />
          </div>
        </div>
      </Tile>

      {/* Disable MFA Modal */}
      <Modal
        open={disableModalOpen}
        onRequestClose={handleCloseDisableModal}
        modalHeading="Disable Two-Factor Authentication"
        modalLabel="Security Settings"
        primaryButtonText={disabling ? 'Disabling...' : 'Disable MFA'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleDisableMFA}
        onSecondarySubmit={handleCloseDisableModal}
        primaryButtonDisabled={!password || !confirmation || disabling}
        danger
        size="sm"
      >
        <div className={styles['modal-notification']}>
          <InlineNotification
            kind="warning"
            title="Warning: This will make your account less secure"
            subtitle="Disabling two-factor authentication will remove an important security layer from your account. All active sessions will be terminated."
            lowContrast
            hideCloseButton
          />
        </div>

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            className={styles['modal-notification']}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <Stack gap={5}>
          <PasswordInput
            id="disable-mfa-password"
            labelText="Confirm your password"
            placeholder="Enter your current password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            disabled={disabling}
            invalid={error !== null && !password}
            invalidText="Password is required"
          />

          <div className={styles['warning-box']}>
            <div className={styles['warning-content']}>
              <Warning size={20} className={styles['warning-icon']} />
              <div>
                <p className={styles['warning-title']}>I understand that:</p>
                <ul className={styles['warning-list']}>
                  <li>My account will be less secure without MFA</li>
                  <li>All my active sessions will be terminated</li>
                  <li>I will need to log in again</li>
                  <li>My backup codes will be deleted</li>
                </ul>
              </div>
            </div>
          </div>

          <Toggle
            id="disable-mfa-confirmation"
            labelText="I confirm I want to disable two-factor authentication"
            toggled={confirmation}
            onToggle={(checked: boolean) => setConfirmation(checked)}
            disabled={disabling}
            size="sm"
          />
        </Stack>
      </Modal>

      {/* Enable MFA Password Confirmation Modal */}
      <Modal
        open={enablePasswordModalOpen}
        onRequestClose={handleCloseEnablePasswordModal}
        modalHeading="Confirm Your Password"
        modalLabel="Enable Two-Factor Authentication"
        primaryButtonText={verifyingPassword ? 'Verifying...' : 'Continue to MFA Setup'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleVerifyPasswordForMFA}
        onSecondarySubmit={handleCloseEnablePasswordModal}
        primaryButtonDisabled={!password || verifyingPassword}
        size="sm"
      >
        <div className={styles['modal-notification']}>
          <InlineNotification
            kind="info"
            title="Password verification required"
            subtitle="For security reasons, please confirm your password before enabling two-factor authentication."
            lowContrast
            hideCloseButton
          />
        </div>

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            className={styles['modal-notification']}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <PasswordInput
          id="enable-mfa-password"
          labelText="Enter your password"
          placeholder="Enter your current password"
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          disabled={verifyingPassword}
          invalid={error !== null && !password}
          invalidText="Password is required"
        />
      </Modal>

      {/* MFA Setup Modal */}
      <MFASetup
        isOpen={mfaSetupModalOpen}
        onClose={() => setMfaSetupModalOpen(false)}
        onComplete={handleMFASetupComplete}
        userId={userId}
        userEmail={userEmail}
      />
    </>
  );
};

export default SecuritySettings;
